import React, { useState, useMemo, useEffect } from 'react';
import { Tooltip } from 'antd';
import dayGridPlugin from '@fullcalendar/daygrid';
import { FullCalendar } from 'ui';
import './styles.scss';
import { useSession } from 'hooks/Lectures/useSession';
import Spinner from 'components/Spinner';
import { useParams } from 'react-router-dom';
import { LectureAdminNavigation } from 'pages/Customers/CustomerUsersEdit/LectureTab';

interface MySessionsCalendarProps {
    userTypeId?: number;
    adminView?: boolean;
    calendarDate?: string;
    setCalendarDate?: React.Dispatch<React.SetStateAction<string>>;
    adminNavigation?: LectureAdminNavigation;
    setAdminNavigation?: React.Dispatch<React.SetStateAction<LectureAdminNavigation>>;
}

const MySessionsCalendar: React.FC<MySessionsCalendarProps> = ({
    adminView = false,
    userTypeId,
    calendarDate,
    setCalendarDate,
    adminNavigation,
    setAdminNavigation,
}) => {
    const [events, setEvents] = useState({});
    const { getColorByStatus, getMySessionsCalendarData, sessionCalendarLoading } = useSession();
    const params = useParams();
    const date = params?.date;

    const initialDate = useMemo(() => {
        if (adminNavigation?.date && adminView) {
            const year = adminNavigation.date.substring(0, 4);
            const month = adminNavigation.date.substring(4, 6);
            return `${year}-${month}-01`;
        } else if (date && date.length === 6) {
            const year = date.substring(0, 4);
            const month = date.substring(4, 6);
            return `${year}-${month}-01`;
        }
        return undefined;
    }, [date]);

    useEffect(() => {
        if (date && date.length === 6) {
            getData(date);
        }
    }, []);

    const renderTooltip = (eventInfo: any) => (
        <div className="flex flex-column">
            <span>{eventInfo.timeText}</span>
            {eventInfo.event.extendedProps.tooltipContent.map((p: string) => (
                <span>{p}</span>
            ))}
        </div>
    );

    const handleDatesSet = (arg: any) => {
        const year = arg.view.currentStart.getFullYear();
        const month = String(arg.view.currentStart.getMonth() + 1).padStart(2, '0'); // month 0-based, so +1 and pad
        const yearMonth = `${year}${month}`;

        if (adminView && adminNavigation && setAdminNavigation) {
            setAdminNavigation({ ...adminNavigation, date: yearMonth });
        }

        getData(yearMonth);
    };

    const getData = async (yearMonth: any) => {
        const calendarData: any = await getMySessionsCalendarData(
            undefined,
            adminView,
            userTypeId,
            Number(yearMonth)
        );

        if (setCalendarDate) {
            setCalendarDate(yearMonth);
        }
        setEvents(calendarData);
    };

    const renderEventContent = (eventInfo: any) => {
        const defaultStatus = eventInfo.event.extendedProps.status;

        return (
            <Tooltip
                className={'flex align-center w-100'}
                placement="top"
                title={renderTooltip(eventInfo)}
            >
                <div className="w-full h-full">
                    <div
                        className="fc-daygrid-event-dot"
                        style={{ borderColor: getColorByStatus(defaultStatus) }}
                    ></div>
                </div>
            </Tooltip>
        );
    };

    return (
        <div className="my-session-calendar">
            <Spinner spinning={sessionCalendarLoading}>
                <FullCalendar
                    plugins={[dayGridPlugin]}
                    dayHeaderContent={(d: any) => d.text.charAt(0)}
                    headerToolbar={{
                        left: 'prev',
                        center: 'title',
                        right: 'next',
                    }}
                    titleFormat={{
                        year: 'numeric',
                        month: 'short',
                    }}
                    contentHeight={'auto'}
                    adminView={adminView}
                    eventContent={renderEventContent}
                    events={events}
                    displayEventEnd={true}
                    datesSet={handleDatesSet}
                    calendarDate={calendarDate}
                    initialDate={initialDate}
                />
            </Spinner>
        </div>
    );
};

export default MySessionsCalendar;
